import { ROUTE_CONFIG } from "@/routes/config";

import { DashboardIssue, DashboardIssueDynamicsOwnerPrivilegePayload } from "../schema/dashboard";

/** Structurally an `IssueDisplayProps`; kept apart so this module does not import its caller. */
export interface DynamicsOwnerPrivilegeIssueDisplay {
	title: string;
	description: string;
	primaryActionLabel: string;
	primaryActionTo: string;
}

type Owner = DashboardIssueDynamicsOwnerPrivilegePayload["owners"][number];

const asOwner = (value: unknown): Owner | null => {
	if (typeof value !== "object" || value === null) {
		return null;
	}

	const entry = value as Record<string, unknown>;

	if (typeof entry.ownerId !== "string") {
		return null;
	}

	return {
		ownerId: entry.ownerId,
		ownerName: typeof entry.ownerName === "string" ? entry.ownerName : null,
		privileges: Array.isArray(entry.privileges)
			? entry.privileges.filter((privilege): privilege is string => typeof privilege === "string")
			: [],
		records: typeof entry.records === "number" ? entry.records : 0,
	};
};

const ownersOf = (issue: DashboardIssue): Owner[] => {
	const owners = issue.payload?.owners;

	return Array.isArray(owners)
		? owners.map(asOwner).filter((owner): owner is Owner => !!owner)
		: [];
};

/**
 * PREC-4361: Dataverse refused these Dynamics users as the owner of synced records, so the
 * records went to another owner. The fix is in Dynamics (a security role) or in the user mapping,
 * so the card names the users and the privileges they lack.
 */
export function dynamicsOwnerPrivilegeIssueDisplay(
	issue: DashboardIssue
): DynamicsOwnerPrivilegeIssueDisplay {
	const owners = ownersOf(issue);
	const privileges = [...new Set(owners.flatMap((owner) => owner.privileges))];
	const title =
		owners.length === 1
			? `${owners[0].ownerName ?? "A mapped user"}'s Dynamics user lacks the permissions to own records`
			: owners.length > 1
				? `${owners.length} Dynamics users lack the permissions to own records`
				: "A Dynamics user lacks the permissions to own records";
	const missing = privileges.length > 0 ? ` (missing ${privileges.join(", ")})` : "";

	return {
		title,
		description: `Dynamics refused them as owner${missing}, so their records were saved under another owner. Assign them a security role that can own records (e.g. Salesperson), or change the user mapping.`,
		primaryActionLabel: "Review user mapping",
		primaryActionTo: ROUTE_CONFIG.INTEGRATIONS_MSDYNAMICS,
	};
}
